import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { MessageMedium } from 'constants/messageMediums';
import { DragAndDropEditor } from 'modules/dragAndDropEditor';
import { HtmlEditor, InAppContentDefinition } from 'modules/graphql/types';
import { SideBySideEditor } from 'modules/sideBySideEditor';
import { ModalType, useModalContext } from 'modules/templateEditor/context';
import { WYSIWYGEditor } from 'modules/wysiwygEditor';
import { FullScreenLayout } from 'ui/fullScreen/FullScreenLayout';

import { LayoutContentContainerFullWidth } from '../common';
import { ContentEditorNotSelected } from '../common/ContentEditorNotSelected';
import { ContentFooter } from '../common/ContentFooter';
import { ContentHeader, EditSavedRowDesignContentHeader } from '../common/ContentHeader';
import { ContentControlBar } from '../common/Controls/ContentControlBar';
import { EditSavedRowDesignWarning } from '../common/EditSavedRowDesignWarning';
import { SettingsDrawer } from '../common/SettingsDrawer';
import { DragAndDropCallback, TemplateEditViews } from '../common/types';
import { useContentEditorPreferencesContext } from '../context/ContentEditorPreferencesContext';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useEditSavedRowDesignContext } from '../context/EditSavedRowDesignContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useContentEditorChangeHandlers } from '../hooks/useContentEditorChangeHandlers';
import { useInitialDragAndDropLoad } from '../hooks/useInitialDragAndDropLoad/useInitialDragAndDropLoad';
import { ContentEditorPreferencesModal } from '../modals/ContentEditorPreferencesModal';
import { SmsSmartOptInModal } from '../modals/SmsSmartOptInModal';
import { useSmsSmartOptInContext } from '../modals/SmsSmartOptInModal/contexts/SmsSmartOptInContext';

export const TEMPLATE_EDITOR_INAPP_CONTENT = 'template-editor-in-app-content';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledFullScreenLayout = styled(FullScreenLayout)<{ isVisible: boolean }>`
  ${({ isVisible }) =>
    isVisible
      ? ``
      : `display: none;`}/** 'display: none;' remains in DOM, but is removed from the layout */
`;

const StyledLayoutContentContainer = styled(LayoutContentContainerFullWidth)`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  margin-bottom: ${({ theme }) => theme.spacing.utils.pxToRem(12)};
  margin-top: ${({ theme }) => theme.spacing.utils.pxToRem(25)};
  min-height: ${({ theme }) => theme.spacing.utils.pxToRem(400)};
`;

export const ContentCore = () => {
  const {
    contentEditor,
    getDragAndDropContentRef,
    dragAndDropInitTs,
    dragAndDropInitValue,
    initialTemplate,
    liveTemplate,
    isDirty,
  } = useEditableFieldsContext();
  const inAppData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const initialInAppData = (initialTemplate?.mediumData || {}) as InAppContentDefinition;
  const locale = inAppData?.locale?.name || ''; // important to use the locale from the mediumData so we know that the localized beeBuilderMetadata is also up to date
  const lastSavedHTML = initialInAppData.html;
  const valueHTML = inAppData.html;

  const { onHtmlChangeRef, onJsonChangeRef } = useContentEditorChangeHandlers();
  const editSavedRowDesign = useEditSavedRowDesignContext();

  const initialDragAndDropLoad = useInitialDragAndDropLoad<InAppContentDefinition>();

  const { openModal } = useModalContext();

  const { isModalOpen: isPreferencesModalOpen, selectedSideBySideTheme } =
    useContentEditorPreferencesContext();
  const { isModalOpen: isSmsSmartOpenInModalOpen } = useSmsSmartOptInContext();
  const { view } = useQueryParamContext();
  const [shouldInitEditor, setShouldInitEditor] = useState<boolean>(false);

  useEffect(() => {
    // The Content component is always rendered (to prevent reloading of the editor when switching
    // views) so we only want to make decisions regarding editor selection when we actually try to
    // view the content
    if (!contentEditor && view === TemplateEditViews.content) {
      openModal(ModalType.SelectContentEditorModals);
    }
  }, [contentEditor, view, openModal]);

  useEffect(() => {
    // Since this component is always rendered, we need an indicator to determine when we first view
    // it in order to trigger the actual initialization of the editors (once the Wysiwyg editor is
    // rendered/mounted, it will auto-correct the valueHTML if needed which can trigger a change)
    if (!shouldInitEditor && view === TemplateEditViews.content) {
      setShouldInitEditor(true);
    }
  }, [shouldInitEditor, view]);

  const renderEditor = () => {
    if (contentEditor === HtmlEditor.Bee && !!inAppData.beeBuilderMetadata) {
      return (
        shouldInitEditor && (
          <DragAndDropEditor
            contentLocale={locale}
            initTs={dragAndDropInitTs}
            initValue={dragAndDropInitValue}
            onChange={onJsonChangeRef.current}
            onGetContentRef={getDragAndDropContentRef}
            messageMedium={MessageMedium.InApp}
            editSavedRowDesign={editSavedRowDesign}
            onUpdateValues={(json: string, html: string, updateCallback: DragAndDropCallback) => {
              onHtmlChangeRef.current(html);
              // onJsonChangeRef.current(json); // ignore this json (it has added properties)
              updateCallback({ html });
            }}
            onEditorLoad={initialDragAndDropLoad.current}
            hasUnsavedTemplateChanges={isDirty}
          />
        )
      );
    }
    if (contentEditor === HtmlEditor.Wysiwyg) {
      return (
        shouldInitEditor && (
          <WYSIWYGEditor
            canLoadHTML
            canUseAutoCompleteParams
            contentLocale={locale}
            height="100%"
            isFullHtmlDoc
            lastSavedValue={lastSavedHTML}
            onChange={onHtmlChangeRef.current}
            value={valueHTML}
          />
        )
      );
    }
    if (contentEditor === HtmlEditor.Ace) {
      // Since the side-by-side editor performs some measurements upon mount, it is important that
      // the component is available in the layout so that it can be measured at that time. The
      // following rendering workaround is necessary because the Content component is always
      // rendered to solve rendering speed (drag-and-drop) and undo issues (wysiwyg) of the other
      // editors. To handle visibility in the UI, containers of all of the editors are added/removed
      // from the layout with the css display property, so it is important to only render the
      // side-by-side editor when it is also intended to be visible in the layout (so that it can be
      // measured) - otherwise it won't be initially visible.
      return (
        shouldInitEditor &&
        view === TemplateEditViews.content && (
          <SideBySideEditor
            canLoadHTML
            height="100%"
            isFullHtmlDoc
            name="content-editor-ace"
            onChange={onHtmlChangeRef.current}
            theme={selectedSideBySideTheme}
            value={valueHTML}
          />
        )
      );
    }
    return <ContentEditorNotSelected />;
  };

  return (
    <>
      {renderEditor()}
      {isPreferencesModalOpen && <ContentEditorPreferencesModal />}
      {isSmsSmartOpenInModalOpen && <SmsSmartOptInModal />}
    </>
  );
};

type Props = {
  isVisible: boolean;
};

export const Content = ({ isVisible }: Props) => {
  const { contentEditor } = useEditableFieldsContext();
  const { isEditingSavedRowDesign } = useEditSavedRowDesignContext();

  return (
    <StyledFullScreenLayout
      actionBarIsAnchored
      DrawerContent={<SettingsDrawer />}
      HeaderContent={
        isEditingSavedRowDesign ? <EditSavedRowDesignContentHeader /> : <ContentHeader />
      }
      FooterContent={
        <ContentFooter
          showAssetBtn={contentEditor === HtmlEditor.Bee}
          showPreferencesBtn={!!contentEditor}
          showSnippetBtn={!!contentEditor}
          showDetailsBtn={!isEditingSavedRowDesign}
        />
      }
      isVisible={isVisible}
    >
      <ContentContainer data-test={TEMPLATE_EDITOR_INAPP_CONTENT}>
        {isEditingSavedRowDesign ? <EditSavedRowDesignWarning /> : <ContentControlBar />}
        <StyledLayoutContentContainer>
          <ContentCore />
        </StyledLayoutContentContainer>
      </ContentContainer>
    </StyledFullScreenLayout>
  );
};

export default Content;
